import { useState, type FormEvent } from "react";
import { Navigate } from "react-router-dom";
import { Eye, EyeOff, ArrowRight, Users, FileText, CalendarDays, ArrowLeft, CheckCircle } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { useDefaultRoute } from "../../hooks/useDefaultRoute";
import { LoadingSpinner } from "../../components/ui/LoadingSpinner";
import { forgotPassword, confirmForgotPassword } from "../../lib/auth";

const FEATURES = [
  { icon: FileText, text: "View & download your payslips anytime" },
  { icon: CalendarDays, text: "Apply for leave and track approvals" },
  { icon: Users, text: "Manage loans and track deductions" },
];

type View = "login" | "forgot-request" | "forgot-confirm" | "forgot-done";

export default function LoginPage() {
  const { signIn, needsPasswordChange, loading, user } = useAuth();
  const defaultRoute = useDefaultRoute();

  // Login state
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Forgot password state
  const [view, setView] = useState<View>("login");
  const [forgotUsername, setForgotUsername] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [forgotError, setForgotError] = useState<string | null>(null);
  const [forgotLoading, setForgotLoading] = useState(false);

  if (loading) return <LoadingSpinner fullscreen />;
  if (needsPasswordChange) return <Navigate to="/change-password" replace />;
  if (user) return <Navigate to={defaultRoute} replace />;

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    setIsSubmitting(true);
    try {
      await signIn(username.trim(), password);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Invalid username or password.";
      if (msg.includes("Incorrect username or password")) {
        setLoginError("Incorrect username or password. Please try again.");
      } else if (msg.includes("User does not exist")) {
        setLoginError("User not found. Please check your username.");
      } else if (msg.includes("User is disabled")) {
        setLoginError("Your account has been disabled. Contact your administrator.");
      } else {
        setLoginError(msg);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotRequest = async (e: FormEvent) => {
    e.preventDefault();
    setForgotError(null);
    setForgotLoading(true);
    try {
      await forgotPassword(forgotUsername.trim());
      setView("forgot-confirm");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to send reset code.";
      if (msg.includes("User does not exist") || msg.includes("Username/client id combination not found")) {
        setForgotError("No account found with that username.");
      } else if (msg.includes("LimitExceededException") || msg.includes("Attempt limit exceeded")) {
        setForgotError("Too many attempts. Please try again later.");
      } else {
        setForgotError(msg);
      }
    } finally {
      setForgotLoading(false);
    }
  };

  const handleForgotConfirm = async (e: FormEvent) => {
    e.preventDefault();
    setForgotError(null);
    if (newPassword !== confirmPassword) {
      setForgotError("Passwords do not match.");
      return;
    }
    if (newPassword.length < 8) {
      setForgotError("Password must be at least 8 characters.");
      return;
    }
    setForgotLoading(true);
    try {
      await confirmForgotPassword(forgotUsername.trim(), resetCode.trim(), newPassword);
      setView("forgot-done");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to reset password.";
      if (msg.includes("CodeMismatchException") || msg.includes("Invalid verification code")) {
        setForgotError("Invalid or expired code. Please check the code and try again.");
      } else if (msg.includes("ExpiredCodeException")) {
        setForgotError("The code has expired. Please request a new one.");
      } else if (msg.includes("InvalidPasswordException")) {
        setForgotError("Password does not meet requirements. Use at least 8 characters with uppercase, lowercase, and a number.");
      } else {
        setForgotError(msg);
      }
    } finally {
      setForgotLoading(false);
    }
  };

  const resetForgotFlow = () => {
    setView("login");
    setForgotUsername("");
    setResetCode("");
    setNewPassword("");
    setConfirmPassword("");
    setForgotError(null);
  };

  return (
    <div className="flex min-h-screen">
      {/* ── Left panel (brand) ── */}
      <div className="hidden lg:flex lg:w-[52%] flex-col relative overflow-hidden bg-[#2447d9]">
        <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-white/5" />
        <div className="absolute top-1/3 -right-32 h-80 w-80 rounded-full bg-white/5" />
        <div className="absolute -bottom-20 left-1/4 h-64 w-64 rounded-full bg-white/5" />

        <div className="relative z-10 flex flex-col justify-between h-full px-12 py-12">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm">
              <span className="text-lg font-extrabold text-white">M</span>
            </div>
            <div>
              <span className="text-xl font-extrabold text-white tracking-tight">MGC</span>
              <p className="text-xs text-white/60 leading-none mt-0.5">Hub</p>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <h2 className="text-4xl font-extrabold text-white leading-tight">
                Your HR portal,<br />simplified.
              </h2>
              <p className="mt-4 text-base text-white/70 leading-relaxed max-w-sm">
                Everything you need — payslips, loans, leave, tasks, and more — in one place.
              </p>
            </div>

            <ul className="space-y-4">
              {FEATURES.map(({ icon: Icon, text }) => (
                <li key={text} className="flex items-center gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/15">
                    <Icon className="h-4 w-4 text-white" />
                  </div>
                  <span className="text-sm text-white/80">{text}</span>
                </li>
              ))}
            </ul>
          </div>

          <p className="text-xs text-white/40">
            MGC Hub &copy; {new Date().getFullYear()}. Internal use only.
          </p>
        </div>
      </div>

      {/* ── Right panel ── */}
      <div className="flex flex-1 flex-col items-center justify-center px-6 py-12 bg-white">
        {/* Mobile logo */}
        <div className="mb-8 flex items-center gap-3 lg:hidden">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#2447d9]">
            <span className="text-lg font-extrabold text-white">M</span>
          </div>
          <div>
            <span className="text-xl font-extrabold text-[#2447d9]">MGC</span>
            <p className="text-xs text-[#64748b]">Hub</p>
          </div>
        </div>

        <div className="w-full max-w-sm">
          {/* ── Login form ── */}
          {view === "login" && (
            <>
              <div className="mb-8">
                <h1 className="text-2xl font-bold text-[#0f172a]">Welcome back</h1>
                <p className="mt-1.5 text-sm text-[#64748b]">
                  Sign in with your MGC credentials to continue.
                </p>
              </div>

              {loginError && (
                <div className="mb-5 flex items-start gap-2.5 rounded-lg bg-[#fef2f2] border border-[#fca5a5] px-4 py-3 text-sm text-[#dc2626]">
                  <span className="shrink-0 mt-0.5">⚠</span>
                  <span>{loginError}</span>
                </div>
              )}

              <form onSubmit={handleLogin} className="space-y-5" noValidate>
                <div className="space-y-1.5">
                  <label htmlFor="username" className="block text-sm font-medium text-[#0f172a]">
                    Username
                  </label>
                  <input
                    id="username"
                    type="text"
                    autoComplete="username"
                    autoFocus
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter your username"
                    required
                    className="w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-4 py-2.5 text-sm text-[#0f172a] placeholder:text-[#94a3b8] transition-colors focus:bg-white focus:border-[#2447d9] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20"
                  />
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label htmlFor="password" className="block text-sm font-medium text-[#0f172a]">
                      Password
                    </label>
                    <button
                      type="button"
                      onClick={() => { setForgotUsername(username); setView("forgot-request"); setForgotError(null); }}
                      className="text-xs text-[#2447d9] font-medium hover:underline focus:outline-none"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative">
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      required
                      className="w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-4 py-2.5 pr-11 text-sm text-[#0f172a] placeholder:text-[#94a3b8] transition-colors focus:bg-white focus:border-[#2447d9] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      tabIndex={-1}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94a3b8] hover:text-[#64748b] transition-colors"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={!username || !password || isSubmitting}
                  className="group relative mt-1 flex w-full items-center justify-center gap-2 overflow-hidden rounded-lg bg-[#2447d9] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-[#1e3a8a] focus:outline-none focus:ring-2 focus:ring-[#2447d9] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <LoadingSpinner size="sm" className="text-white" />
                      <span>Signing in…</span>
                    </>
                  ) : (
                    <>
                      <span>Sign In</span>
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </>
                  )}
                </button>
              </form>

              <p className="mt-6 text-center text-xs text-[#94a3b8]">
                Don&apos;t have credentials?{" "}
                <span className="text-[#2447d9] font-medium">Contact your HR administrator.</span>
              </p>
            </>
          )}

          {/* ── Step 1: Request reset code ── */}
          {view === "forgot-request" && (
            <>
              <button
                type="button"
                onClick={resetForgotFlow}
                className="mb-6 flex items-center gap-1.5 text-sm text-[#64748b] hover:text-[#0f172a] transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to sign in
              </button>

              <div className="mb-8">
                <h1 className="text-2xl font-bold text-[#0f172a]">Reset your password</h1>
                <p className="mt-1.5 text-sm text-[#64748b]">
                  Enter your username and we'll send a verification code to your registered email.
                </p>
              </div>

              {forgotError && (
                <div className="mb-5 flex items-start gap-2.5 rounded-lg bg-[#fef2f2] border border-[#fca5a5] px-4 py-3 text-sm text-[#dc2626]">
                  <span className="shrink-0 mt-0.5">⚠</span>
                  <span>{forgotError}</span>
                </div>
              )}

              <form onSubmit={handleForgotRequest} className="space-y-5" noValidate>
                <div className="space-y-1.5">
                  <label htmlFor="forgot-username" className="block text-sm font-medium text-[#0f172a]">
                    Username
                  </label>
                  <input
                    id="forgot-username"
                    type="text"
                    autoComplete="username"
                    autoFocus
                    value={forgotUsername}
                    onChange={(e) => setForgotUsername(e.target.value)}
                    placeholder="Enter your username"
                    required
                    className="w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-4 py-2.5 text-sm text-[#0f172a] placeholder:text-[#94a3b8] transition-colors focus:bg-white focus:border-[#2447d9] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20"
                  />
                </div>

                <button
                  type="submit"
                  disabled={!forgotUsername || forgotLoading}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#2447d9] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-[#1e3a8a] focus:outline-none focus:ring-2 focus:ring-[#2447d9] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {forgotLoading ? (
                    <>
                      <LoadingSpinner size="sm" className="text-white" />
                      <span>Sending code…</span>
                    </>
                  ) : (
                    <span>Send Verification Code</span>
                  )}
                </button>
              </form>
            </>
          )}

          {/* ── Step 2: Enter code + new password ── */}
          {view === "forgot-confirm" && (
            <>
              <button
                type="button"
                onClick={() => { setView("forgot-request"); setForgotError(null); }}
                className="mb-6 flex items-center gap-1.5 text-sm text-[#64748b] hover:text-[#0f172a] transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>

              <div className="mb-8">
                <h1 className="text-2xl font-bold text-[#0f172a]">Check your email</h1>
                <p className="mt-1.5 text-sm text-[#64748b]">
                  We sent a verification code to your registered email. Enter it below along with your new password.
                </p>
              </div>

              {forgotError && (
                <div className="mb-5 flex items-start gap-2.5 rounded-lg bg-[#fef2f2] border border-[#fca5a5] px-4 py-3 text-sm text-[#dc2626]">
                  <span className="shrink-0 mt-0.5">⚠</span>
                  <span>{forgotError}</span>
                </div>
              )}

              <form onSubmit={handleForgotConfirm} className="space-y-5" noValidate>
                <div className="space-y-1.5">
                  <label htmlFor="reset-code" className="block text-sm font-medium text-[#0f172a]">
                    Verification Code
                  </label>
                  <input
                    id="reset-code"
                    type="text"
                    autoComplete="one-time-code"
                    autoFocus
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value)}
                    placeholder="Enter the 6-digit code"
                    required
                    className="w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-4 py-2.5 text-sm text-[#0f172a] placeholder:text-[#94a3b8] transition-colors focus:bg-white focus:border-[#2447d9] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20"
                  />
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="new-password" className="block text-sm font-medium text-[#0f172a]">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      id="new-password"
                      type={showNewPassword ? "text" : "password"}
                      autoComplete="new-password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="At least 8 characters"
                      required
                      className="w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-4 py-2.5 pr-11 text-sm text-[#0f172a] placeholder:text-[#94a3b8] transition-colors focus:bg-white focus:border-[#2447d9] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword((v) => !v)}
                      tabIndex={-1}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94a3b8] hover:text-[#64748b] transition-colors"
                      aria-label={showNewPassword ? "Hide password" : "Show password"}
                    >
                      {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="confirm-password" className="block text-sm font-medium text-[#0f172a]">
                    Confirm New Password
                  </label>
                  <input
                    id="confirm-password"
                    type="password"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter new password"
                    required
                    className="w-full rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-4 py-2.5 text-sm text-[#0f172a] placeholder:text-[#94a3b8] transition-colors focus:bg-white focus:border-[#2447d9] focus:outline-none focus:ring-2 focus:ring-[#2447d9]/20"
                  />
                </div>

                <button
                  type="submit"
                  disabled={!resetCode || !newPassword || !confirmPassword || forgotLoading}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#2447d9] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-[#1e3a8a] focus:outline-none focus:ring-2 focus:ring-[#2447d9] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {forgotLoading ? (
                    <>
                      <LoadingSpinner size="sm" className="text-white" />
                      <span>Resetting…</span>
                    </>
                  ) : (
                    <span>Reset Password</span>
                  )}
                </button>
              </form>
            </>
          )}

          {/* ── Step 3: Success ── */}
          {view === "forgot-done" && (
            <div className="flex flex-col items-center gap-6 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#f0fdf4]">
                <CheckCircle className="h-8 w-8 text-[#16a34a]" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-[#0f172a]">Password reset!</h1>
                <p className="mt-2 text-sm text-[#64748b]">
                  Your password has been updated successfully. You can now sign in with your new password.
                </p>
              </div>
              <button
                type="button"
                onClick={resetForgotFlow}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#2447d9] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-[#1e3a8a] focus:outline-none focus:ring-2 focus:ring-[#2447d9] focus:ring-offset-2"
              >
                Back to Sign In
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
